import React, { useContext, useEffect, useState } from "react";
import { useRouter } from "next/router";
import dayjs from "dayjs";
import calendar from "dayjs/plugin/calendar";

import {
  ApiPagination,
  INotificationListItem,
  PlatformContext,
  RequestPagination,
  getMyNewNotificationsApi,
  markNotificationAsRead,
  markNotificationStackAsReadApi,
  SHARED_PLATFORM_ROUTES,
} from "@ascendnow/shared";
import { sortAndGroupByDate } from "../../lib/helpers/sortAndGroupByDate";
import AscButton from "../AscButton/AscButton";
import AscNotificationItem from "../AscNotificationItem/AscNotificationItem";
import AscEmptyList from "../AscEmptyList/AscEmptyList";
import { notificationTypeHandler } from "../../lib/helpers";
import { useAscContext } from "../../context";
import styles from "./AscNotificationDrawer.module.scss";

dayjs.extend(calendar);

export interface AscNotificationContentProps {
  setVisible: (visible: boolean) => void;
}

export default function AscNotificationContent({
  setVisible,
}: AscNotificationContentProps) {
  const router = useRouter();
  const { toast } = useAscContext();
  const { getNotificationsCount } = useContext(PlatformContext);
  const [notifications, setNotifications] = useState<INotificationListItem[]>(
    []
  );
  const [seenNotificationIds, setSeenNofiticationIds] = useState<string[]>([]);
  const [pagination, setPagination] = useState<ApiPagination>({
    page: 1,
    pageSize: 20,
    totalItems: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(false);

  async function getNotifications({ page, pageSize }: RequestPagination) {
    setLoading(true);
    try {
      const res = await getMyNewNotificationsApi({
        page,
        pageSize,
      });
      if (res?.data) {
        if (page === 1) {
          let firstUnreadNotifications: string[] = [];
          setNotifications(
            res.data.notifications?.map((el, index) => {
              if (index < 8 && !el?.read) {
                firstUnreadNotifications.push(el?.id);
                return {
                  ...el,
                  read: true,
                };
              }

              return el;
            })
          );
          if (firstUnreadNotifications.length > 0) {
            setSeenNofiticationIds(firstUnreadNotifications);
          }
        } else {
          setNotifications((prev) => [
            ...notifications,
            ...res.data.notifications,
          ]);
        }
        setPagination(res?.data.pagination);
      }
      setLoading(false);
    } catch (error) {
      toast.error(`Error fetching list of notification, query: /notifications`);
    } finally {
      setLoading(false);
    }
  }

  async function handleMarkNotificationStackAsRead(notificationIds: string[]) {
    try {
      await markNotificationStackAsReadApi(notificationIds);

      setSeenNofiticationIds([]);
      getNotificationsCount?.();
    } catch (error) {
      toast.error(
        `Error marking the list of notification as read, query: /notifications/mark-as-read`
      );
    }
  }

  const handleClickNotification = (notification: INotificationListItem) => {
    if (!notification.read) {
      markNotificationAsRead(notification.id).then((res) => {
        setNotifications(
          notifications.map((el) => {
            if (el.id === notification.id) {
              return {
                ...el,
                read: true,
              };
            }

            return el;
          })
        );
        getNotificationsCount?.();
      });
    }

    const notificationType = notificationTypeHandler?.(
      notification.type,
      notification.body
    );
    if (notificationType?.route !== undefined) {
      router.push(notificationType?.route as string);
      if (setVisible) {
        setVisible(false);
      }
    }
  };

  const handleClickMore = () => {
    const unreadNotifications = notifications.filter(
      (el) => !el.read || el?.seen
    );

    if (unreadNotifications.length > 0) {
      handleMarkNotificationStackAsRead(
        unreadNotifications.map((el) => el?.id)
      );
    }

    setVisible(false);
    router.push(SHARED_PLATFORM_ROUTES.account_actions_updates);
  };

  const handleSeenNotification = (notificationId: string) => {
    setSeenNofiticationIds([...seenNotificationIds, notificationId]);
    setNotifications(
      notifications.map((el) => {
        if (el.id === notificationId && !el?.read) {
          return {
            ...el,
            read: true,
          };
        }

        return el;
      })
    );
  };

  useEffect(() => {
    getNotifications({ page: pagination.page, pageSize: pagination.pageSize });

    return () => {
      if (seenNotificationIds.length > 0) {
        handleMarkNotificationStackAsRead(seenNotificationIds);
      }
    };
  }, []);

  useEffect(() => {
    const TIME_IN_SECONDS = 1;
    let intervalId: NodeJS.Timer;
    intervalId = setInterval(() => {
      if (seenNotificationIds.length > 0) {
        handleMarkNotificationStackAsRead(seenNotificationIds);
      }
    }, TIME_IN_SECONDS * 1000);

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [seenNotificationIds.length]);

  return (
    <div className={styles.content}>
      {notifications.length > 0 ? (
        sortAndGroupByDate(
          notifications.map((el) => ({ ...el, date: el.createdAt }))
        ).map((notificationGroup) => (
          <React.Fragment key={notificationGroup.date}>
            <div className={styles.dateLabel}>
              {dayjs().diff(dayjs(notificationGroup?.date), "day") === 0
                ? "Today"
                : dayjs(notificationGroup?.date).format("D MMMM, YYYY")}
            </div>
            {notificationGroup.values?.map((notification, index) => {
              const notificationType = notificationTypeHandler?.(
                notification.type,
                notification.body
              );
              const notificationTag = notificationType?.tag;
              const notificationIcon = notificationType?.icon;

              return (
                <AscNotificationItem
                  key={notification.id}
                  id={notification.id}
                  date={notification.createdAt}
                  read={notification.read}
                  title={notification.title}
                  imageUrl={notificationIcon}
                  type={notificationTag}
                  onClick={() => handleClickNotification(notification)}
                  onVisible={handleSeenNotification}
                  onKeyDown={(e) => {
                    if (e.keyCode === 13 || e.code === "Enter") {
                      handleClickNotification(notification);
                    }
                  }}
                />
              );
            })}
          </React.Fragment>
        ))
      ) : (
        <AscEmptyList title="No notifications" style={{ height: "75vh" }} />
      )}
      {pagination.totalPages > pagination.page ? (
        <div className={styles.seeMoreButtonContainer}>
          <AscButton
            loading={loading}
            onClick={handleClickMore}
            size="large"
            variant="secondary"
            fullWidth
          >
            See more
          </AscButton>
        </div>
      ) : null}
    </div>
  );
}
